import { useState, useRef, useEffect, memo, useContext } from "react"
import Loader from "../../ui/loader"
import { Link } from "react-router-dom"
import { RegisterFormContext } from "../../auth/register-form/context"
import { SvgCheckbox, SvgCheckboxOff } from "../../icons"
import { useToast } from "../../../hooks/useToast"
import { useLocation } from "react-router-dom"

type PaymentSource = "paypal" | "card" | "apple_pay" | "google-pay"

interface CardFieldsProps {
  isCheckedRef: React.MutableRefObject<boolean>
  createOrder?: (data: any, source: PaymentSource) => Promise<any>
  onApprove: (data: any, source: PaymentSource) => Promise<any>
  createVaultSetupToken?: (data: any, source: PaymentSource) => Promise<any>
  isChecked: boolean
  setIsChecked: (checked: boolean) => void
  isTermsAgreed: boolean
  setIsTermsAgreed: React.Dispatch<React.SetStateAction<boolean>>
  redirectUrl: string
  selectedPlanFromChild: any
  isSubx: boolean
  activeService: any
  isSdkReady: boolean
  readyForPayment?: boolean
  validatingForm?: boolean
  setValidatingForm?: any
  setLoading: any
  payInFull?: boolean
}

interface CardFieldInstance {
  submit: () => Promise<void>
  getState: () => Promise<void>
  isEligible: () => boolean
  NameField: (options: any) => any
  NumberField: (options: any) => any
  CVVField: (options: any) => any
  ExpiryField: (options: any) => any
  close: () => void
}

const PublicPayPalCardFields: React.FC<CardFieldsProps> = ({
  isCheckedRef,
  onApprove,
  createOrder,
  isChecked,
  setIsChecked,
  isSubx,
  activeService,
  createVaultSetupToken,
  isSdkReady,
  readyForPayment,
  validatingForm,
  setLoading,
  setValidatingForm,
  payInFull,
}) => {
  const location = useLocation()
  const [nameError, setNameError] = useState("")
  const [cardFieldsReady, setCardFieldsReady] = useState(false)
  const [cardFieldInstance, setCardFieldInstance] =
    useState<CardFieldInstance | null>(null)
  const { toast } = useToast()
  const { createAccount } = useContext(RegisterFormContext)
  const checkboxRef = useRef<HTMLInputElement | null>(null)
  const pathParts = location.pathname.split("/").filter(Boolean)
  const guestCheckout = pathParts.length === 3 && pathParts[1] === "checkout"

  const isSaveCardLocked =
    (activeService?.payment_term === "flexible" && !payInFull) ||
    activeService?.service_details?.type === "recurring" ||
    activeService?.service_details?.billing?.unit === "installment"

  const handleChange = (event: any) => {
    if (isSaveCardLocked) return

    setIsChecked(event.target.checked)
    isCheckedRef.current = event.target.checked
  }

  useEffect(() => {
    if (isSaveCardLocked) {
      setIsChecked(true)
      isCheckedRef.current = true
    }
  }, [
    activeService?.payment_term,
    activeService?.service_details?.type,
    activeService?.service_details?.billing?.unit,
    payInFull,
  ])

  useEffect(() => {
    isCheckedRef.current = isChecked
  }, [isChecked])

  useEffect(() => {
    if (!isSdkReady) return

    let attempts = 0
    const maxAttempts = 10
    const interval = 200

    const tryCreateCardFields = () => {
      attempts += 1
      if (window.paypal && window.paypal.CardFields) {
        console.log("SDK loaded, creating CardFields")
        const cardField = window.paypal.CardFields({
          ...(createOrder
            ? { createOrder: (data: any) => createOrder?.(data, "card") }
            : {
                createVaultSetupToken: (data: any) =>
                  createVaultSetupToken?.(data, "card"),
              }),
          onApprove: (data: any) => onApprove(data, "card"),
        })

        if (cardField.isEligible()) {
          const nameField = cardField.NameField()
          nameField.setAttribute("placeholder", "Full name")
          nameField.render("#card-name-field-container")

          const numberField = cardField.NumberField()
          numberField.render("#card-number-field-container")

          const cvvField = cardField.CVVField()
          cvvField.render("#card-cvv-field-container")

          const expiryField = cardField.ExpiryField()
          expiryField.render("#card-expiry-field-container")

          setCardFieldInstance(cardField)
          setCardFieldsReady(true)
        } else {
          console.warn("CardField is NOT eligible")
        }
      } else if (attempts < maxAttempts) {
        setTimeout(tryCreateCardFields, interval)
      } else {
        console.error("Failed to load CardFields after multiple attempts")
      }
    }

    tryCreateCardFields()

    return () => {
      if (cardFieldInstance) {
        try {
          cardFieldInstance.close()
        } catch (err) {
          console.warn("CardField cleanup failed", err)
        }
      }
    }
  }, [isSdkReady])

  const handleSubmit = () => {
    if (cardFieldInstance) {
      cardFieldInstance.getState().then((data: any) => {
        setLoading(true)
        cardFieldInstance
          .submit()
          .then(() => {
            setLoading(false)
          })
          .catch((error: any) => {
            setLoading(false)
          })
      })
    }
  }

  const checkCardFields = async () => {
    if (!cardFieldInstance) return

    try {
      const data: any = await cardFieldInstance.getState()

      if (!validatingForm) return

      const f = data.fields

      if (f.cardNameField.isEmpty) {
        console.error("Cardholder name is required!")
        setLoading(false)
        toast.error("Cardholder name is required!")
        return
      }

      if (f.cardNumberField.isEmpty) {
        console.error("Card number is required!")
        setLoading(false)
        toast.error("Card number is required!")
        return
      }

      if (f.cardExpiryField.isEmpty) {
        console.error("Card Expiration is required!")
        setLoading(false)
        toast.error("Card Expiration is required!")
        return
      }

      if (f.cardCvvField.isEmpty) {
        console.error("Card CVV is required!")
        setLoading(false)
        toast.error("Card CVV is required!")
        return
      }

      if (guestCheckout) {
        await createAccount()
      }
    } catch (err) {
      console.error("Error checking card fields:", err)
      setLoading(false)
      toast.error("There was an issue validating your card. Please try again.")
    }
  }

  useEffect(() => {
    if (readyForPayment === true) {
      handleSubmit()
    }
  }, [readyForPayment])

  useEffect(() => {
    if (validatingForm === true) {
      checkCardFields()
    }
  }, [validatingForm])

  return (
    <>
      {!cardFieldsReady && (
        <div className="flex justify-center py-6">
          <Loader />
        </div>
      )}
      <div className="w-full font-bold mt-4">
        Enter your card information below:
      </div>
      <div className="inline-block">
        <img
          src="https://www.paypalobjects.com/webstatic/mktg/logo/AM_mc_vs_dc_ae.jpg"
          alt="Payments by PayPal"
          className="h-20 w-auto"
        />
      </div>
      <div id="checkout-form">
        <div id="card-name-field-container"></div>
        <div id="card-number-field-container"></div>
        <div id="card-expiry-field-container"></div>
        <div id="card-cvv-field-container"></div>
      </div>

      {nameError && <div className="text-[red] mt-2">{nameError}</div>}

      <div className="flex flex-col items-center justify-center w-full space-y-2">
        <div className="flex w-[95%] mb-2 mt-2">
          <input
            id="checkbox-input"
            ref={checkboxRef}
            onChange={handleChange}
            checked={isChecked}
            type="checkbox"
            className="hidden"
          />
          <label
            htmlFor={isSaveCardLocked ? undefined : "checkbox-input"}
            className={`self-start mr-4 flex items-center ${
              isSaveCardLocked
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer"
            }`}
          >
            {isChecked ? (
              <SvgCheckbox className="cursor-pointer" />
            ) : (
              <SvgCheckboxOff
                className="cursor-pointer border border-[gray] rounded-md"
                fill="#CCCCCC"
              />
            )}
          </label>

          <div className="text-graySlate text-base">
            <p>Save card for future payments</p>
          </div>
        </div>

        <div className="justify-center flex items-center text-center px-4">
          <p className="text-muted-foreground text-xs text-pretty w-full mx-auto">
            By continuing, I have read and agreed to the{" "}
            <Link
              to="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blurple"
            >
              Agreement & Disclosures
            </Link>{" "}
            and{" "}
            <Link
              to="https://intercom.help/zoee/en/articles/8623117-how-does-zoee-protect-your-information-on-paypal"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blurple"
            >
              Payment Authorization
            </Link>{" "}
            provided by Zoee.
          </p>
        </div>
      </div>
    </>
  )
}

export default memo(PublicPayPalCardFields)
